import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from 'src/hooks/useCurrency';
import { useChangePurchaseAgentState, useIsPurchaseAgentStateChanging } from 'src/hooks/usePurchaseAgents';
import { PurchaseAgentListItem } from 'src/modules/generated/api';
import { getStateActions } from 'src/modules/purchase-agent';

const useStyles = makeStyles((theme) => ({
  // The play/pause/stop glyphs fill less of their box than edit/delete, so they are drawn larger
  // until they look the same size. The negative margin keeps the button the same size as its
  // edit/delete neighbours.
  icon: {
    fontSize: 26,
    margin: -3,
  },
  // the stop square only fills half of its box
  stopIcon: {
    fontSize: 30,
    margin: -5,
  },
  // Colours sit on the button, not the icon, so MUI's disabled grey still wins.
  play: {
    color: theme.palette.intelligence.main,
  },
  stop: {
    color: theme.palette.error.main,
  },
}));

type PurchaseAgentStateButtonProps = {
  agent: PurchaseAgentListItem;
};

const ActivateConfirmDialog = ({
  agent,
  open,
  onCancel,
  onConfirm,
}: {
  agent: PurchaseAgentListItem;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();
  const formatMoney = useFormatMoney();
  const maxBudget = formatMoney(agent.limitUsage?.maxBudget);
  const maxVehicleCount = agent.limitUsage?.maxVehicleCount;

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{t('purchaseAgent.activate.confirmTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('purchaseAgent.activate.confirmText')}</DialogContentText>
        {maxBudget ? (
          <Typography variant="body2">{t('purchaseAgent.limits.maxBudget', { value: maxBudget })}</Typography>
        ) : null}
        {maxVehicleCount != null ? (
          <Typography variant="body2">{t('purchaseAgent.limits.maxVehicles', { value: maxVehicleCount })}</Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="secondary" variant="outlined" size="small">
          {t('common.cancel')}
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained" size="small">
          {t('purchaseAgent.activate.tooltip')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Pause, resume or (after confirmation) activate, depending on the agent's state. */
export const PurchaseAgentPrimaryStateButton = ({ agent }: PurchaseAgentStateButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate: changeState } = useChangePurchaseAgentState();
  const id = agent.id?.purchaseAgentId;
  const isChanging = useIsPurchaseAgentStateChanging(id);
  const { primary } = getStateActions(agent.state);
  // Only `activate` starts a new cycle and thereby resets the used limits, so only it is confirmed.
  const confirmPrimary = primary === 'activate';

  const runPrimary = () => {
    if (id) changeState({ id, action: primary });
  };

  const tooltips = {
    pause: t('purchaseAgent.pause.tooltip'),
    resume: t('purchaseAgent.resume.tooltip'),
    activate: t('purchaseAgent.activate.hint'),
  };

  return (
    <>
      <Tooltip title={tooltips[primary]} placement="top">
        {/* a disabled button fires no events, the span keeps the tooltip working */}
        <span>
          <IconButton
            size="small"
            className={primary === 'pause' ? undefined : classes.play}
            disabled={!id || isChanging}
            onClick={() => (confirmPrimary ? setConfirmOpen(true) : runPrimary())}
          >
            {primary === 'pause' ? <PauseIcon className={classes.icon} /> : <PlayArrowIcon className={classes.icon} />}
          </IconButton>
        </span>
      </Tooltip>
      <ActivateConfirmDialog
        agent={agent}
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          runPrimary();
        }}
      />
    </>
  );
};

export const PurchaseAgentDeactivateButton = ({ agent }: PurchaseAgentStateButtonProps): JSX.Element | null => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { mutate: changeState } = useChangePurchaseAgentState();
  const id = agent.id?.purchaseAgentId;
  const isChanging = useIsPurchaseAgentStateChanging(id);
  const { canDeactivate } = getStateActions(agent.state);

  if (!canDeactivate) return null;

  return (
    <Tooltip title={t('purchaseAgent.deactivate.hint')} placement="top">
      <span>
        <IconButton
          size="small"
          className={classes.stop}
          disabled={!id || isChanging}
          onClick={() => {
            if (id) changeState({ id, action: 'deactivate' });
          }}
        >
          <StopIcon className={classes.stopIcon} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
